import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
    validateMatterContextFile,
    hashFileSha256,
    type MatterContextFileInvalidReason,
} from '../utils/matterContextFiles';
import { useChatStore } from './chatStore';
import { captureEvent } from '../analytics';

/**
 * Modal state for the V3 Matter Context UI. The conversational chat-style
 * input from V2 is replaced by a structured three-section left pane
 * (matter header / knowledge / files) with a primary "Extract" button.
 *
 * - `matterKnowledge` is hydrated on open from the latest extraction run's
 *   persisted knowledge text (GET /MatterContext → additional_text);
 *   `processedKnowledge` baselines that loaded/already-extracted value so it
 *   isn't mistaken for new, unsaved content.
 * - `extractionState` + `progressPhase` + `etaSecondsRemaining` drive the
 *   bottom-of-pane progress bar. Phases rotate on a client-side timer
 *   because /MatterContext/Status only reports completed runs and has
 *   no in-flight progress.
 * - One-shot per modal open: the Extract button hides once extraction has
 *   completed; reopening the modal resets state.
 */

export type ExtractionState = 'idle' | 'analyzing' | 'complete' | 'error';
export type ProgressPhase =
    | 'comprehending_knowledge'
    | 'analyzing_files'
    | 'generating_summary'
    | 'isolating_entities';
export type SuggestionStatus = 'pending' | 'accepted';

const PROGRESS_PHASES: ProgressPhase[] = [
    'comprehending_knowledge',
    'analyzing_files',
    'generating_summary',
    'isolating_entities',
];

// No trailing ellipsis — the progress row renders its own animated one.
export const PHASE_LABELS: Record<ProgressPhase, string> = {
    comprehending_knowledge: 'Reviewing matter knowledge',
    analyzing_files: 'Reading the files',
    generating_summary: 'Summarizing',
    isolating_entities: 'Extracting events and entities',
};

export interface StagedFile {
    file: File;
    valid: boolean;
    reason?: MatterContextFileInvalidReason;
    // SHA-256 of the file bytes, computed at stage time (undefined if
    // hashing failed or the file is invalid and wasn't hashed).
    hash?: string;
    // True when the file's bytes already exist in this matter (or repeat
    // within the staged batch). Duplicates are excluded from upload but —
    // unlike invalid files — do NOT block extracting the rest.
    duplicate?: boolean;
    // True once this file's bytes have been sent through an extraction this
    // session. It stays visible in the list as a contributed document but is
    // excluded from any subsequent upload and from the "new content" gate
    // (the backend already has its bytes). Distinct from `duplicate`, which
    // flags bytes the matter had *before* this session.
    processed?: boolean;
}

// Editable event fields tracked for the modified-vs-extract diff.
export const EVENT_TRACKED_FIELDS = ['date', 'context'] as const;
export type EventTrackedField = (typeof EVENT_TRACKED_FIELDS)[number];
export type EventSnapshot = Record<EventTrackedField, string>;

export interface EventRow {
    id: string;
    slug: string;
    date: string;
    context: string;
    // Baseline captured at extract (or create) time — drives the modified
    // highlight and the dialog's reset action.
    original: EventSnapshot;
    suggestionStatus?: SuggestionStatus;
    // Soft delete (see EntityRow.deleted): the row stays visible and
    // restorable; deleted rows are excluded from the publish payload.
    deleted?: boolean;
    // Persisted backend ids for the single-event upsert. `backendId` is the
    // matter_context_items.id this row maps to — the matching key sent to
    // POST /MatterContext/Event when publish() stages queued changes;
    // `backendDraftOfId` is the established item a draft edits, or null.
    // Undefined for rows created locally that haven't been staged yet —
    // their absence is what marks the row as queued (see
    // isEventQueuedForPublish).
    backendId?: string;
    backendDraftOfId?: string | null;
}

function snapshotEvent(row: { date: string; context: string }): EventSnapshot {
    return { date: row.date, context: row.context };
}

/** True when a single tracked field differs from its extract baseline. */
export function isEventFieldModified(row: EventRow, field: EventTrackedField): boolean {
    return row[field] !== row.original[field];
}

/** True when any tracked field on the event differs from the baseline. */
export function isEventModified(row: EventRow): boolean {
    return EVENT_TRACKED_FIELDS.some((f) => row[f] !== row.original[f]);
}

/**
 * True when the event holds local changes that publish() must stage as a
 * backend draft: tracked-field edits vs the baseline, or a row added
 * locally that has never been persisted (no backendId). Edits are queued —
 * never saved per-item — and flushed together when the user publishes.
 */
export function isEventQueuedForPublish(row: EventRow): boolean {
    return !row.deleted && (isEventModified(row) || !row.backendId);
}

// Editable entity fields tracked for the modified-vs-extract diff.
export const ENTITY_TRACKED_FIELDS = [
    'name',
    'context',
    'entityType',
    'firstName',
    'lastName',
    'email',
] as const;
export type EntityTrackedField = (typeof ENTITY_TRACKED_FIELDS)[number];
export type EntitySnapshot = Record<EntityTrackedField, string>;

export interface EntityRow {
    id: string;
    slug: string;
    entityType: string;
    name: string;
    context: string;
    // Frontend-only basic-information fields. Backend persistence is
    // deferred, so these are not sent on publish yet and default to ''.
    // Empty is a normal state — the UI offers an "+ Add" affordance.
    firstName: string;
    lastName: string;
    email: string;
    // Baseline captured at extract (or create) time. Drives the
    // modified-field highlight and the dialog's reset action.
    original: EntitySnapshot;
    suggestionStatus?: SuggestionStatus;
    // Soft delete: the row stays visible (struck through) and can be
    // restored; deleted rows are excluded from the publish payload.
    deleted?: boolean;
    // Persisted backend ids for the single-entity upsert, mirroring
    // EventRow. `backendId` is the matter_context_items.id this row maps
    // to — the matching key sent to POST /MatterContext/Entity when
    // publish() stages queued changes; `backendDraftOfId` is the
    // established item a draft edits, or null. Undefined for rows created
    // locally that haven't been staged yet (see isEntityQueuedForPublish).
    backendId?: string;
    backendDraftOfId?: string | null;
}

function snapshotEntity(row: {
    name: string;
    context: string;
    entityType: string;
    firstName: string;
    lastName: string;
    email: string;
}): EntitySnapshot {
    return {
        name: row.name,
        context: row.context,
        entityType: row.entityType,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
    };
}

/** True when a single tracked field differs from its extract baseline. */
export function isEntityFieldModified(row: EntityRow, field: EntityTrackedField): boolean {
    return row[field] !== row.original[field];
}

/** True when any tracked field on the entity differs from the baseline. */
export function isEntityModified(row: EntityRow): boolean {
    return ENTITY_TRACKED_FIELDS.some((f) => row[f] !== row.original[f]);
}

/** Entity counterpart of isEventQueuedForPublish. */
export function isEntityQueuedForPublish(row: EntityRow): boolean {
    return !row.deleted && (isEntityModified(row) || !row.backendId);
}

export interface RelationshipRow {
    sourceEntitySlug: string;
    relationVerb: string;
    targetEntitySlug: string;
    detail: string | null;
}

// Snapshot of what the current summary is known to reflect, keyed by the
// row's local id. The summary-update diff compares the live rows against
// this to classify each as edited / added / deleted (see
// _buildSummaryChanges). Captured whenever the panes are (re)seeded from
// the backend and after each successful summary update.
interface SummaryReflectedState {
    entities: Record<string, { name: string; entity_type: string; context: string }>;
    events: Record<string, { date: string; context: string }>;
}

// POST /MatterContext/Extract result — success/failure plus the run id
// and terminal status. The extracted data is read back separately.
// `skipped_files` lists uploads the backend skipped as duplicates of
// files already processed for this matter.
interface ExtractResponse {
    run_id: string;
    status: string;
    skipped_files?: string[];
}

// GET /MatterContext payload — the persisted matter context for the
// latest history row, used to seed the review panes after extraction.
interface MatterContextData {
    summary: string;
    // The run that produced `summary`. Sent back as matter_context_run_id
    // when the user gives feedback on the summary. Null/absent when there is
    // no summary or it was authored from scratch (no originating run).
    summary_run_id?: string | null;
    // The knowledge text the user last supplied to an extraction run, so the
    // Knowledge section can be rehydrated on open. "" / absent when none.
    additional_text?: string | null;
    entities: Array<{
        id?: string;
        draft_of_id?: string | null;
        slug?: string;
        entity_type: string;
        name: string;
        email_address?: string | null;
        context: string;
    }>;
    key_events: Array<{
        id?: string;
        draft_of_id?: string | null;
        slug?: string;
        date: string;
        context: string;
    }>;
    relationships?: Array<{
        source_entity_slug: string;
        relation_verb: string;
        target_entity_slug: string;
        detail?: string | null;
    }>;
    // Files already uploaded + processed for this matter (across completed
    // runs). Display-only — shown in the Files section so a loaded matter
    // reflects what its context was extracted from.
    documents?: Array<{
        file_name: string;
        mime_type?: string;
        file_size_bytes?: number;
        content_hash?: string | null;
    }>;
    // True when the backend holds staged draft items a publish would
    // promote (e.g. an extraction that was never published). Keeps the
    // Publish button enabled on an otherwise-unedited loaded context.
    has_unpublished_drafts?: boolean;
}

// A previously-uploaded document, surfaced read-only in the Files section.
// Distinct from StagedFile: there is no File object, only server metadata.
export interface ExistingDocument {
    fileName: string;
    mimeType: string;
    fileSizeBytes: number;
    hash: string | null;
}

// Backend entity_type values, lowercase snake_case. Used to round-trip
// the value back on publish (the UI displays a Title-Case label).
const ENTITY_TYPE_BACKEND: Record<string, string> = {
    Person: 'individual',
    Organization: 'organization',
    Group: 'group_of_people',
    Place: 'place',
    Department: 'department',
};

function backendEntityType(displayType: string): string {
    return ENTITY_TYPE_BACKEND[displayType] || 'individual';
}

// Backend uses lowercase snake_case for entity_type; UI displays Title-Case.
// The backend `individual` type is surfaced to users as "Person".
const ENTITY_TYPE_DISPLAY: Record<string, string> = {
    individual: 'Person',
    organization: 'Organization',
    group_of_people: 'Group',
    place: 'Place',
    department: 'Department',
};

function displayEntityType(backendType: string): string {
    const key = (backendType || '').toLowerCase();
    return ENTITY_TYPE_DISPLAY[key] || 'Person';
}

// Pull the most specific message out of a FastAPI error body. `detail`
// is typically a string (400 / 413 / 422 / generic 500) but is a nested
// object for ExtractionFailedError (500): {detail, run_id, status}.
async function _extractErrorMessage(
    response: Response,
    fallbackPrefix = 'Request failed',
): Promise<string> {
    const fallback = `${fallbackPrefix} (HTTP ${response.status})`;
    try {
        const body = await response.json();
        const detail = body?.detail;
        if (typeof detail === 'string' && detail.trim()) return detail;
        if (detail && typeof detail === 'object') {
            const inner = detail.detail;
            if (typeof inner === 'string' && inner.trim()) return inner;
        }
    } catch {
        /* non-JSON body */
    }
    return fallback;
}

// Mocked ETA: a rough function of file count + whether knowledge is set.
// Used both as the total duration the phase rotator pages through and as
// the initial value for the per-second countdown shown to the user.
function _estimateExtractionSeconds(fileCount: number, hasKnowledge: boolean): number {
    const base = 30;
    const perFile = 30;
    const knowledgeSurcharge = hasKnowledge ? 30 : 0;
    return Math.max(60, base + fileCount * perFile + knowledgeSurcharge);
}

let _idCounter = 0;
const nextId = () => `mc-${Date.now()}-${++_idCounter}`;

export const useMatterContextModalStore = defineStore('matterContextModal', () => {
    const visible = ref(false);
    const targetMatterId = ref<string | null>(null);
    // True when the modal was opened automatically as part of onboarding (the
    // matter's context isn't set yet) rather than by an explicit user action.
    // Drives the open animation, which only plays for the automatic case.
    const openedAutomatically = ref(false);

    const matterKnowledge = ref('');
    // Knowledge text that has already been extracted/published — the value
    // hydrated on open, or the text sent through the last extraction this
    // session. Mirrors the StagedFile `processed` flag: it baselines what's
    // already been processed so it doesn't count as new content for the
    // Analyze gate (canRunExtraction) or as unsaved work on close
    // (hasUnsavedWork). Only knowledge *beyond* this baseline is "new".
    const processedKnowledge = ref('');
    const stagedFiles = ref<StagedFile[]>([]);

    // Documents already uploaded + processed for this matter, hydrated from
    // GET /MatterContext on open. Read-only — shown above the staging area so
    // a loaded matter reflects the files its context was extracted from.
    const existingDocuments = ref<ExistingDocument[]>([]);

    // SHA-256 digests of files already processed for this matter, loaded
    // when the modal opens. Used to pre-flag staged duplicates before
    // upload (the backend re-checks authoritatively).
    const knownHashes = ref<Set<string>>(new Set());

    // Inline knowledge editor (replaces the old dialog). Lifted to store
    // level so the modal close handler can detect a dirty editor and
    // route through the discard-changes dialog.
    const knowledgeEditorOpen = ref(false);
    const knowledgeEditorDraft = ref('');

    const extractionState = ref<ExtractionState>('idle');
    const progressPhase = ref<ProgressPhase>(PROGRESS_PHASES[0]);
    const etaSecondsRemaining = ref(0);
    // User has dismissed the post-completion progress bar; left pane
    // re-shows the Extract button in a disabled state to signal that
    // extraction has already run for this modal open.
    const progressDismissed = ref(false);

    const summary = ref('');
    // Run that produced the current summary, surfaced by GET /MatterContext
    // (and seeded on extraction read-back). Sent as matter_context_run_id when
    // the user gives summary feedback. Null when there's no summary to attach
    // feedback to — the feedback controls disable in that case.
    const summaryRunId = ref<string | null>(null);
    // One-shot flag: set when extraction completes, consumed by the Summary
    // tab to play its reveal animation only on first render after completion.
    const summaryRevealPending = ref(false);
    // True while the summary is being reconciled with reviewed entity/event
    // changes (POST /MatterContext/UpdateSummary). Lets the Summary tab show a
    // subtle "updating" affordance; non-blocking otherwise.
    const summaryUpdating = ref(false);
    // What the current summary is known to reflect (see SummaryReflectedState).
    const summaryReflected = ref<SummaryReflectedState>({ entities: {}, events: {} });

    const events = ref<EventRow[]>([]);
    const entities = ref<EntityRow[]>([]);
    const relationships = ref<RelationshipRow[]>([]);

    // Per-tab confirmation flags. Dormant for now — the per-tab Confirm
    // buttons are commented out (may be revived later) and Publish no longer
    // gates on them. Extraction lands every row already confirmed/included,
    // so these are set true on completion to keep the (hidden) confirm flow
    // coherent if it's restored. See eventsVisited / entitiesVisited for the
    // current Publish gate.
    const eventsConfirmed = ref(false);
    const entitiesConfirmed = ref(false);

    // Per-tab "has the user looked at this tab" flags. Drive the unvisited
    // dot on the Key events / Entities tabs — purely a visual review nudge,
    // not a Publish gate (Publish is enabled as soon as extraction completes).
    // A fresh extraction / re-extraction resets them to false so the dot
    // reappears for the new items; loaded existing context lands pre-visited
    // (it's already published, so no dot when reviewing it).
    const eventsVisited = ref(false);
    const entitiesVisited = ref(false);

    const errorMessage = ref<string | null>(null);
    const publishing = ref(false);
    const toast = ref<string | null>(null);

    // True when the modal opened onto pre-existing matter context loaded
    // from GET /MatterContext (rather than a fresh extraction this session).
    // Suppresses the Extract button / completion progress bar — re-extraction
    // is out of scope for the review/edit-existing flow.
    const loadedExisting = ref(false);
    // Serialized publish payload captured right after loading existing
    // context, used as the dirty-check baseline so opening to view (with no
    // edits) doesn't trip the close-without-saving warning. Null for fresh
    // extractions, whose unpublished results always count as unsaved.
    const loadedSnapshot = ref<string | null>(null);
    // Clean-state baseline for the re-extract warning. Unlike loadedSnapshot
    // (which stays null for fresh extractions so they remain immediately
    // publishable), this is captured after EVERY extraction completes as well
    // as on load — so adding/editing rows after any extraction is detected as
    // work a re-extraction would discard. Null only before the first
    // extraction / load. See hasUnpublishedReviewEdits.
    const reviewBaseline = ref<string | null>(null);

    // True when the loaded context carries server-side draft items that a
    // publish would promote (an extraction that was closed before
    // publishing). Lets canPublish pass without in-session edits — the
    // publish itself is what promotes the staged drafts. Doesn't affect
    // the close warning: drafts are persisted server-side, so closing
    // loses nothing.
    const loadedHasDrafts = ref(false);

    // True while the modal is hydrating its initial state from
    // GET /MatterContext (during open()). Form elements are disabled and a
    // "please wait" notice is shown until this clears.
    const loadingExisting = ref(false);

    // Mocked progress timers (see file-level comment on the rationale).
    let _phaseTimer: ReturnType<typeof setInterval> | null = null;
    let _etaTimer: ReturnType<typeof setInterval> | null = null;

    const phaseLabel = computed(() => {
        if (extractionState.value === 'complete') return 'Analysis complete';
        return PHASE_LABELS[progressPhase.value];
    });

    // Deleted (rejected/removed) rows don't count as awaiting review.
    const eventsHasPending = computed(() =>
        events.value.some((e) => e.suggestionStatus === 'pending' && !e.deleted),
    );
    const entitiesHasPending = computed(() =>
        entities.value.some((e) => e.suggestionStatus === 'pending' && !e.deleted),
    );

    const hasAnyPending = computed(() => eventsHasPending.value || entitiesHasPending.value);

    const hasInvalidFiles = computed(() => stagedFiles.value.some((f) => !f.valid));

    const knowledgeEditorDirty = computed(
        () => knowledgeEditorOpen.value && knowledgeEditorDraft.value !== matterKnowledge.value,
    );

    // Inputs (knowledge editor + file staging + drop zone) are editable in
    // every state except while a run is in flight or the initial state is
    // still hydrating from the server. This is what lets the user add new
    // docs / knowledge on top of already-loaded context to trigger a
    // re-extraction; 'analyzing' and the initial load both lock them.
    const inputsEditable = computed(
        () => extractionState.value !== 'analyzing' && !loadingExisting.value,
    );

    // Serialized snapshot of the editable state, used to detect whether loaded
    // context has been edited since it was hydrated (see hasUnsavedWork /
    // canPublish). It wraps the publish payload PLUS the contact fields
    // (firstName/lastName/email) — those aren't carried in the publish payload
    // (firstName/lastName aren't persisted at all yet; email persists via the
    // separate entity-upsert path, not _buildPublishPayload), but editing them
    // (e.g. via the entity "Split name" action) must still count as an unsaved
    // change so Publish activates.
    function _dirtySnapshot(): string {
        return JSON.stringify({
            payload: _buildPublishPayload(),
            contactFields: entities.value
                .filter((e) => !e.deleted)
                .map((e) => [e.slug, e.firstName, e.lastName, e.email]),
        });
    }
    const currentSnapshot = computed(() => _dirtySnapshot());

    // True when the review panes diverge from their last clean baseline (the
    // post-load or post-extraction snapshot) — i.e. the user has since edited
    // or added summary/entities/events/relationships. Drives the re-extraction
    // warning: re-extracting reloads from the published set and would discard
    // these in-session edits (including rows added but never published).
    const hasUnpublishedReviewEdits = computed(
        () => reviewBaseline.value !== null && currentSnapshot.value !== reviewBaseline.value,
    );

    // True when closing the modal would discard meaningful work: staged
    // knowledge/files, an in-flight or completed extraction (results awaiting
    // publish), or a dirty inline knowledge editor. Drives the close warning.
    const hasUnsavedWork = computed(() => {
        if (knowledgeEditorDirty.value) return true;
        // Loaded/already-extracted knowledge isn't unsaved; only text the user
        // has added or edited beyond that baseline counts.
        if (matterKnowledge.value.trim() !== processedKnowledge.value.trim()) return true;
        if (stagedFiles.value.length > 0) return true;
        if (extractionState.value === 'analyzing') return true;
        if (extractionState.value === 'complete') {
            // Context loaded from the server isn't "unsaved" until the user
            // actually edits it — compare against the load-time baseline.
            // Fresh extractions have no baseline (null) and always count.
            if (loadedSnapshot.value !== null) {
                return currentSnapshot.value !== loadedSnapshot.value;
            }
            return true;
        }
        return false;
    });

    const canRunExtraction = computed(() => {
        // Block while a run is in flight or the initial state is still loading.
        // Re-extraction over loaded / already-complete context is allowed,
        // gated on there being new content to extract (below).
        if (extractionState.value === 'analyzing') return false;
        if (loadingExisting.value) return false;
        // Any flagged file blocks extraction even if other valid content exists,
        // matching the backend's all-or-nothing validation semantics.
        if (hasInvalidFiles.value) return false;
        // Knowledge already extracted/loaded (processedKnowledge) isn't new
        // content on its own — only text added/edited beyond it re-arms Analyze.
        const hasKnowledge =
            matterKnowledge.value.trim().length > 0 &&
            matterKnowledge.value.trim() !== processedKnowledge.value.trim();
        // Duplicates (bytes the matter already had) and already-processed files
        // (sent earlier this session) are excluded from the upload, so they
        // don't count as new extractable content on their own.
        const hasValidFile = stagedFiles.value.some((f) => f.valid && !f.duplicate && !f.processed);
        return hasKnowledge || hasValidFile;
    });

    const canPublish = computed(() => {
        if (extractionState.value !== 'complete') return false;
        if (hasAnyPending.value) return false;
        // Context loaded from the server lands fully confirmed; there's nothing
        // to publish until the user actually changes something — unless the
        // load carried staged server-side drafts (an extraction closed before
        // publishing), which the publish itself promotes. A fresh extraction /
        // re-extraction has no loaded baseline (loadedSnapshot is null) and is
        // always publishable. Any edit makes currentSnapshot diverge from the
        // baseline and re-enables publishing.
        if (
            !loadedHasDrafts.value &&
            loadedSnapshot.value !== null &&
            currentSnapshot.value === loadedSnapshot.value
        ) {
            return false;
        }
        return true;
    });

    const canConfirmEvents = computed(() => {
        if (extractionState.value !== 'complete') return false;
        if (eventsConfirmed.value) return false;
        return true;
    });

    const canConfirmEntities = computed(() => {
        if (extractionState.value !== 'complete') return false;
        if (entitiesConfirmed.value) return false;
        return true;
    });

    // Summary feedback (thumbs up/down) requires a run id to attach to — the
    // Feedback endpoint can't accept feedback without one. False when the
    // summary has no originating run (so the Summary tab disables the
    // thumbs controls; copy stays available).
    const canSubmitSummaryFeedback = computed(() => summaryRunId.value !== null);

    // Last summary rating recorded this session — fills the chosen thumb so the
    // UI reflects the last feedback submitted. Re-submittable; overwritten each
    // time. The in-flight direction drives the spinner on the matching thumb
    // and guards against double-submits. Neither is set on the silent path.
    const summaryFeedback = ref<'positive' | 'negative' | null>(null);
    const summaryFeedbackSubmitting = ref<'positive' | 'negative' | null>(null);

    async function open(matterId: string, opts: { auto?: boolean } = {}) {
        _resetState();
        targetMatterId.value = matterId;
        openedAutomatically.value = opts.auto === true;
        visible.value = true;
        // Block the form and show a "please wait" notice while the initial
        // state hydrates from the server.
        loadingExisting.value = true;
        // Best-effort: load known file hashes so staged duplicates can be
        // flagged before upload. Fire-and-forget — extraction works (and
        // the backend dedups) even if this never resolves.
        void _loadKnownHashes(matterId);
        try {
            await _loadExistingContext(matterId);
        } finally {
            loadingExisting.value = false;
        }
    }

    async function _loadKnownHashes(matterId: string) {
        try {
            const response = await fetch(`/api/Matter/${matterId}/MatterContext/DocumentHashes`);
            if (!response.ok) return;
            const body = (await response.json()) as { hashes?: string[] };
            knownHashes.value = new Set(body.hashes ?? []);
        } catch {
            /* best-effort — duplicates still caught server-side */
        }
    }

    function close() {
        visible.value = false;
        _resetState();
        targetMatterId.value = null;
    }

    // Reset the modal back to its blank idle onboarding state without closing
    // it or clearing the target matter. Used after the matter's context has
    // been deleted server-side, so the open modal reflects the now-empty matter
    // and the user can immediately re-upload.
    function resetInPlace() {
        _resetState();
    }

    function _resetState() {
        _stopProgressTimers();
        openedAutomatically.value = false;
        matterKnowledge.value = '';
        processedKnowledge.value = '';
        stagedFiles.value = [];
        existingDocuments.value = [];
        knownHashes.value = new Set();
        knowledgeEditorOpen.value = false;
        knowledgeEditorDraft.value = '';
        eventsConfirmed.value = false;
        entitiesConfirmed.value = false;
        eventsVisited.value = false;
        entitiesVisited.value = false;
        progressDismissed.value = false;
        extractionState.value = 'idle';
        progressPhase.value = PROGRESS_PHASES[0];
        etaSecondsRemaining.value = 0;
        summary.value = '';
        summaryRunId.value = null;
        summaryRevealPending.value = false;
        summaryUpdating.value = false;
        summaryFeedback.value = null;
        summaryFeedbackSubmitting.value = null;
        summaryReflected.value = { entities: {}, events: {} };
        events.value = [];
        entities.value = [];
        relationships.value = [];
        errorMessage.value = null;
        publishing.value = false;
        loadedExisting.value = false;
        loadedHasDrafts.value = false;
        loadedSnapshot.value = null;
        reviewBaseline.value = null;
        loadingExisting.value = false;
    }

    // On modal open, hydrate the review panes from the matter's most recent
    // persisted context (latest history row, published or not). When the
    // matter has none, the response is empty and we leave the blank idle
    // onboarding flow untouched. Loaded content lands fully reviewed —
    // rows accepted, both tabs confirmed, state 'complete' — so the user can
    // edit and re-publish without re-reviewing. A failed load never blocks
    // onboarding; we swallow the error and stay idle.
    async function _loadExistingContext(matterId: string) {
        try {
            const data = await _fetchMatterContext(matterId);
            const hasContent = Boolean(
                data.summary || data.key_events?.length || data.entities?.length,
            );
            if (!hasContent) return;
            // Guard against a late resolve after the modal was closed or
            // re-targeted at a different matter in the meantime.
            if (!visible.value || targetMatterId.value !== matterId) return;

            _seedFromExtract(data, { accepted: 'all' });
            // Rehydrate the Knowledge section from the last extraction run's
            // persisted text, and baseline it as already-processed so it
            // doesn't read as new content (Analyze) or unsaved work (close).
            matterKnowledge.value = data.additional_text ?? '';
            processedKnowledge.value = matterKnowledge.value;
            // Reflect the files this matter's context was extracted from. These
            // are display-only (no File object) and live above the staging area.
            existingDocuments.value = (data.documents ?? []).map((d) => ({
                fileName: d.file_name,
                mimeType: d.mime_type ?? '',
                fileSizeBytes: d.file_size_bytes ?? 0,
                hash: d.content_hash ?? null,
            }));
            eventsConfirmed.value = true;
            entitiesConfirmed.value = true;
            // Loaded context is already published — mark both tabs visited so
            // the unvisited dot doesn't show when reviewing existing context.
            eventsVisited.value = true;
            entitiesVisited.value = true;
            extractionState.value = 'complete';
            loadedExisting.value = true;
            // Staged server-side drafts (extraction closed before publishing)
            // keep Publish enabled even though nothing was edited this session.
            loadedHasDrafts.value = data.has_unpublished_drafts === true;
            // Suppress the post-extraction completion progress bar — nothing
            // was just extracted this session.
            progressDismissed.value = true;
            loadedSnapshot.value = _dirtySnapshot();
            reviewBaseline.value = loadedSnapshot.value;
        } catch {
            /* leave the modal in its blank idle state */
        }
    }

    function setMatterKnowledge(text: string) {
        matterKnowledge.value = text;
    }

    function openKnowledgeEditor() {
        if (!inputsEditable.value) return;
        knowledgeEditorDraft.value = matterKnowledge.value;
        knowledgeEditorOpen.value = true;
    }

    function saveKnowledgeEditor() {
        matterKnowledge.value = knowledgeEditorDraft.value;
        knowledgeEditorOpen.value = false;
        knowledgeEditorDraft.value = '';
    }

    function discardKnowledgeEditor() {
        knowledgeEditorOpen.value = false;
        knowledgeEditorDraft.value = '';
    }

    async function stageFiles(files: File[]) {
        if (!inputsEditable.value) return;
        for (const f of files) {
            const alreadyStaged = stagedFiles.value.find(
                (s) => s.file.name === f.name && s.file.size === f.size,
            );
            if (alreadyStaged) continue;

            const validation = validateMatterContextFile(f);

            // Hash valid files to flag duplicates (already in the matter,
            // or repeated within this batch). Hashing failures are
            // non-fatal — the backend still dedups on extract.
            let hash: string | undefined;
            let duplicate = false;
            if (validation.valid) {
                try {
                    hash = await hashFileSha256(f);
                } catch {
                    /* hashing unavailable — fall back to name/size below */
                }
                const hashMatch =
                    !!hash &&
                    (knownHashes.value.has(hash) || stagedFiles.value.some((s) => s.hash === hash));
                // Fallback for legacy documents extracted before content
                // hashing: they carry a NULL hash (so they're absent from
                // knownHashes) but are listed in existingDocuments. Match them
                // on file name + size so the user is still warned and the file
                // is excluded from re-upload. Restricted to hash-less existing
                // docs so a genuinely different file that happens to share a
                // name with a *hashed* doc isn't wrongly flagged.
                const legacyNameMatch = existingDocuments.value.some(
                    (d) => d.hash === null && d.fileName === f.name && d.fileSizeBytes === f.size,
                );
                duplicate = hashMatch || legacyNameMatch;
            }

            stagedFiles.value.push({
                file: f,
                valid: validation.valid,
                reason: validation.reason,
                hash,
                duplicate,
            });
        }
    }

    function removeStagedFile(name: string) {
        if (!inputsEditable.value) return;
        stagedFiles.value = stagedFiles.value.filter((f) => f.file.name !== name);
    }

    function _startProgressTimers(totalSeconds: number) {
        _stopProgressTimers();
        const stepMs = Math.max(1000, Math.floor((totalSeconds * 1000) / PROGRESS_PHASES.length));
        let stepIdx = 0;
        progressPhase.value = PROGRESS_PHASES[0];
        _phaseTimer = setInterval(() => {
            stepIdx = Math.min(stepIdx + 1, PROGRESS_PHASES.length - 1);
            progressPhase.value = PROGRESS_PHASES[stepIdx];
        }, stepMs);

        etaSecondsRemaining.value = totalSeconds;
        _etaTimer = setInterval(() => {
            etaSecondsRemaining.value = Math.max(0, etaSecondsRemaining.value - 1);
        }, 1000);
    }

    function _stopProgressTimers() {
        if (_phaseTimer) {
            clearInterval(_phaseTimer);
            _phaseTimer = null;
        }
        if (_etaTimer) {
            clearInterval(_etaTimer);
            _etaTimer = null;
        }
    }

    async function runExtraction() {
        if (!canRunExtraction.value) return;
        const matterId = targetMatterId.value;
        if (!matterId) {
            errorMessage.value = 'No matter selected';
            return;
        }

        // canRunExtraction already blocks when there are invalid entries,
        // but filter defensively in case the gate is ever loosened. Known
        // duplicates are excluded so we don't re-upload bytes the matter
        // already has.
        const filesToSend = stagedFiles.value
            .filter((f) => f.valid && !f.duplicate && !f.processed)
            .map((f) => f.file);
        const knowledgeText = matterKnowledge.value.trim();

        // Re-extraction: panes already hold rows (loaded existing context or a
        // prior run this session). The re-seed replaces them with the
        // cumulative published set.
        const isReExtraction = entities.value.length > 0 || events.value.length > 0;

        extractionState.value = 'analyzing';
        errorMessage.value = null;
        // A re-run starts from a loaded/complete state where the completion bar
        // was already dismissed — re-show it so the run shows progress.
        progressDismissed.value = false;
        const eta = _estimateExtractionSeconds(filesToSend.length, knowledgeText.length > 0);
        _startProgressTimers(eta);

        try {
            const formData = new FormData();
            for (const file of filesToSend) {
                formData.append('files', file);
            }
            if (knowledgeText) {
                formData.append('additional_text', knowledgeText);
            }

            const response = await fetch(`/api/Matter/${matterId}/MatterContext/Extract`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(await _extractErrorMessage(response, 'Extraction failed'));
            }

            // Extract reports success/failure; on a non-empty run the
            // persisted matter context is read back from GET
            // /MatterContext to seed the review panes.
            const extractResult: ExtractResponse = await response.json();

            // Onboarding-funnel step. Counts/booleans only — never file
            // names or knowledge text (see analytics PII policy).
            captureEvent('matter_context_extracted', {
                matter_id: matterId,
                file_count: filesToSend.length,
                has_knowledge_text: knowledgeText.length > 0,
                is_reextraction: isReExtraction,
                status: extractResult.status,
            });

            // Surface any server-side duplicate skips (covers files the
            // client pre-check missed, e.g. uploaded before hashes loaded).
            const skipped = extractResult.skipped_files ?? [];
            if (skipped.length > 0) {
                const n = skipped.length;
                showToast(
                    `${n} duplicate file${n > 1 ? 's' : ''} skipped — already added to this matter`,
                );
            }

            if (extractResult.status !== 'empty') {
                const data = await _fetchMatterContext(matterId);
                if (isReExtraction) {
                    // Replace the panes with the cumulative published set so the
                    // earlier-loaded rows aren't duplicated. Every row lands
                    // included/confirmed (see below) — the per-row include/exclude
                    // switch is how the user opts items out.
                    _seedFromExtract(data, { replace: true, accepted: 'all' });
                    // The panes now reflect a fresh extraction, not a pristine
                    // load — drop the loaded baseline so close-without-saving and
                    // re-extract guards treat the new state as unsaved work.
                    loadedExisting.value = false;
                    loadedSnapshot.value = null;
                } else {
                    _seedFromExtract(data, { accepted: 'all' });
                }
            }

            // Extracted context lands fully confirmed/included by default — the
            // per-row switch is how the user excludes items. The dormant confirm
            // flags are kept coherent for a possible revival of the Confirm flow.
            eventsConfirmed.value = true;
            entitiesConfirmed.value = true;
            // Reset the visited flags so the unvisited dot reappears on the
            // tabs holding the freshly extracted items. (Publish is already
            // enabled — these only drive the dot, not the gate.)
            eventsVisited.value = false;
            entitiesVisited.value = false;

            // Keep the staged files visible in the left pane — they're the
            // documents the user just contributed. Mark the ones sent this run
            // as `processed` so they read as "Added" and no longer count as new
            // content for a re-extraction (the Extract gate only fires on
            // genuinely new files). Register their hashes so an identical file
            // staged later is flagged a duplicate. Knowledge text is left as-is
            // so the user can still see what they provided.
            const sentHashes = stagedFiles.value
                .filter((f) => f.valid && !f.duplicate && !f.processed)
                .map((f) => f.hash)
                .filter((h): h is string => !!h);
            const merged = new Set(knownHashes.value);
            for (const h of sentHashes) merged.add(h);
            knownHashes.value = merged;
            stagedFiles.value = stagedFiles.value.map((f) =>
                f.valid && !f.duplicate && !f.processed ? { ...f, processed: true } : f,
            );
            // Baseline the knowledge just sent so it no longer counts as new
            // content (re-arming Analyze) until the user edits it further.
            processedKnowledge.value = knowledgeText;

            _stopProgressTimers();
            extractionState.value = 'complete';
            etaSecondsRemaining.value = 0;
            // Capture the just-extracted state as the clean baseline so any
            // subsequent edit/add is flagged as work a further re-extraction
            // would discard (the re-extract warning). Unlike loadedSnapshot,
            // this is set for fresh extractions too — they have no published
            // baseline but still hold discardable work once edited.
            reviewBaseline.value = _dirtySnapshot();
            // One-shot: the Summary tab plays its reveal animation the first
            // time it renders after this completes.
            summaryRevealPending.value = true;
        } catch (err) {
            errorMessage.value = err instanceof Error ? err.message : String(err);
            _stopProgressTimers();
            extractionState.value = 'error';
        }
    }

    // Read back the persisted matter context for the latest history
    // row. Returned shape carries slugs + relationships so the review
    // panes and the publish round-trip stay intact.
    async function _fetchMatterContext(matterId: string): Promise<MatterContextData> {
        const response = await fetch(`/api/Matter/${matterId}/MatterContext`);
        if (!response.ok) {
            throw new Error(
                await _extractErrorMessage(response, 'Failed to load extracted context'),
            );
        }
        return (await response.json()) as MatterContextData;
    }

    // Seed the review panes from a GET /MatterContext payload.
    //
    // `accepted` controls how each seeded row presents:
    //   - 'none' — fresh extraction output: every row lands 'pending' for review.
    //   - 'all'  — context loaded from the server (already-published or prior
    //              work): every row lands 'accepted' (no review dots, publish-ready).
    //   - Set    — re-extraction: rows whose slug was already present land
    //              'accepted', genuinely-new slugs land 'pending'.
    //
    // `replace` clears the existing events/entities first — used by re-extraction,
    // whose payload is the cumulative published set (already including the rows
    // loaded earlier this session), so appending would duplicate them.
    // Relationships are always replaced from the (cumulative) response.
    function _seedFromExtract(
        data: MatterContextData,
        opts: { accepted?: 'all' | 'none' | Set<string>; replace?: boolean } = {},
    ) {
        const { accepted = 'none', replace = false } = opts;
        const statusForSlug = (slug: string): SuggestionStatus => {
            if (accepted === 'all') return 'accepted';
            if (accepted === 'none') return 'pending';
            return accepted.has(slug) ? 'accepted' : 'pending';
        };

        if (replace) {
            events.value = [];
            entities.value = [];
        }

        if (data.summary) {
            summary.value = data.summary;
            // Track the run behind this summary so feedback can reference it.
            summaryRunId.value = data.summary_run_id ?? null;
        }

        const incomingEvents = data.key_events ?? [];
        const newEvents: EventRow[] = incomingEvents.map((e) => {
            const base = { date: e.date || '', context: e.context || '' };
            const slug = e.slug || '';
            return {
                id: nextId(),
                slug,
                backendId: e.id,
                backendDraftOfId: e.draft_of_id ?? null,
                ...base,
                original: snapshotEvent(base),
                suggestionStatus: statusForSlug(slug),
            };
        });
        events.value = [...events.value, ...newEvents];

        const incomingEntities = data.entities ?? [];
        const newEntities: EntityRow[] = incomingEntities.map((e) => {
            const base = {
                entityType: displayEntityType(e.entity_type),
                name: e.name,
                context: e.context || '',
                firstName: '',
                lastName: '',
                // email_address is persisted by extraction (assembly LLM
                // emits it for individuals) and by the entity-upsert edit
                // path, so seed it from the GET payload rather than blanking.
                email: e.email_address ?? '',
            };
            const slug = e.slug || '';
            return {
                id: nextId(),
                slug,
                backendId: e.id,
                backendDraftOfId: e.draft_of_id ?? null,
                ...base,
                original: snapshotEntity(base),
                suggestionStatus: statusForSlug(slug),
            };
        });
        entities.value = [...entities.value, ...newEntities];

        const incomingRelationships = data.relationships ?? [];
        relationships.value = incomingRelationships.map((r) => ({
            sourceEntitySlug: r.source_entity_slug,
            relationVerb: r.relation_verb,
            targetEntitySlug: r.target_entity_slug,
            detail: r.detail ?? null,
        }));

        // The summary just came from the backend and reflects these rows —
        // baseline the diff against them so later edits are measured from here.
        _captureSummaryReflected();
    }

    // Snapshot the non-deleted rows as the state the current summary reflects.
    // Called after every (re)seed and after a successful summary update.
    function _captureSummaryReflected() {
        const ent: SummaryReflectedState['entities'] = {};
        for (const e of entities.value) {
            if (e.deleted) continue;
            ent[e.id] = {
                name: e.name,
                entity_type: backendEntityType(e.entityType),
                context: e.context,
            };
        }
        const evt: SummaryReflectedState['events'] = {};
        for (const ev of events.value) {
            if (ev.deleted) continue;
            evt[ev.id] = { date: ev.date, context: ev.context };
        }
        summaryReflected.value = { entities: ent, events: evt };
    }

    // Diff the live rows against what the summary reflects, classifying each
    // as edited / added / deleted. Rows unchanged since the last capture
    // produce no entry, so an unedited confirm yields an empty diff (no-op).
    function _buildSummaryChanges(): {
        entity_changes: Array<{ change: string; before?: object; after?: object }>;
        event_changes: Array<{ change: string; before?: object; after?: object }>;
    } {
        const entity_changes: Array<{ change: string; before?: object; after?: object }> = [];
        for (const e of entities.value) {
            const ref = summaryReflected.value.entities[e.id];
            if (e.deleted) {
                // Only worth removing if the summary actually reflected it.
                if (ref) entity_changes.push({ change: 'deleted', before: ref });
                continue;
            }
            const after = {
                name: e.name,
                entity_type: backendEntityType(e.entityType),
                context: e.context,
            };
            if (!ref) {
                entity_changes.push({ change: 'added', after });
            } else if (
                ref.name !== after.name ||
                ref.entity_type !== after.entity_type ||
                ref.context !== after.context
            ) {
                entity_changes.push({ change: 'edited', before: ref, after });
            }
        }

        const event_changes: Array<{ change: string; before?: object; after?: object }> = [];
        for (const ev of events.value) {
            const ref = summaryReflected.value.events[ev.id];
            if (ev.deleted) {
                if (ref) event_changes.push({ change: 'deleted', before: ref });
                continue;
            }
            const after = { date: ev.date, context: ev.context };
            if (!ref) {
                event_changes.push({ change: 'added', after });
            } else if (ref.date !== after.date || ref.context !== after.context) {
                event_changes.push({ change: 'edited', before: ref, after });
            }
        }

        return { entity_changes, event_changes };
    }

    // Reconcile the summary with the reviewed entity/event changes. Fired by
    // the both-tabs-confirmed watcher. No-ops (without a network call) when
    // there is no summary to update or nothing changed since the last
    // capture. Non-blocking: a failure keeps the existing summary and surfaces
    // a toast — re-confirming after another edit retries the full diff.
    async function updateSummaryFromChanges(): Promise<void> {
        const matterId = targetMatterId.value;
        if (!matterId) return;
        // Summary maintenance only — we don't synthesize a summary from edits.
        if (!summary.value.trim()) return;

        const { entity_changes, event_changes } = _buildSummaryChanges();
        if (entity_changes.length === 0 && event_changes.length === 0) return;

        summaryUpdating.value = true;
        try {
            const response = await fetch(`/api/Matter/${matterId}/MatterContext/UpdateSummary`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    summary: summary.value,
                    entity_changes,
                    event_changes,
                }),
            });
            if (!response.ok) {
                throw new Error(await _extractErrorMessage(response, 'Summary update failed'));
            }
            const data = (await response.json()) as { summary: string; changed: boolean };
            if (data.changed && data.summary) {
                summary.value = data.summary;
            }
            // The current rows are now what the summary reflects — re-baseline
            // so a later round of edits diffs against this state.
            _captureSummaryReflected();
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'Could not update the summary');
        } finally {
            summaryUpdating.value = false;
        }
    }

    // Record the user's thumbs-up/down on the current summary via POST
    // /Feedback. `is_positive` carries the up/down signal;
    // `issueReported` / `feedbackDetails` are the optional negative-feedback
    // dialog fields. The summary's run id (captured on load/extraction) is
    // sent as matter_context_run_id. No-ops without a matter or run id —
    // canSubmitSummaryFeedback gates the UI so this only guards races. Always
    // resolves: on success or failure a toast is shown and nothing is thrown.
    //
    // `silent` is for the negative-feedback dialog's Cancel path: the negative
    // flag is still posted to the backend, but no toast is shown, so the user
    // sees nothing.
    async function submitSummaryFeedback(payload: {
        isPositive: boolean;
        issueReported?: string;
        feedbackDetails?: string;
        silent?: boolean;
    }): Promise<void> {
        const matterId = targetMatterId.value;
        const runId = summaryRunId.value;
        if (!matterId || !runId) return;

        const direction = payload.isPositive ? 'positive' : 'negative';
        if (!payload.silent) summaryFeedbackSubmitting.value = direction;
        try {
            const response = await fetch(`/api/Matter/${matterId}/Feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    is_positive: payload.isPositive,
                    issue_reported: payload.issueReported || null,
                    feedback_details: payload.feedbackDetails || null,
                    matter_context_run_id: runId,
                }),
            });
            if (!response.ok) {
                throw new Error(await _extractErrorMessage(response, 'Feedback failed'));
            }
            if (!payload.silent) {
                summaryFeedback.value = direction;
                showToast('Thanks for the feedback');
            }
            // `has_details` is a boolean, not the free-text the user typed.
            captureEvent('matter_context_summary_feedback', {
                matter_id: matterId,
                rating: direction,
                silent: payload.silent === true,
                has_details: Boolean(payload.feedbackDetails),
            });
        } catch (err) {
            if (!payload.silent) {
                showToast(err instanceof Error ? err.message : 'Could not submit feedback');
            }
        } finally {
            if (!payload.silent) summaryFeedbackSubmitting.value = null;
        }
    }

    // Editing/adding/deleting a row in a confirmed tab un-confirms it, so the
    // user re-confirms when done — that re-confirm is what re-triggers the
    // summary reconciliation (see the both-confirmed watcher) and re-gates
    // Publish on a fresh review. A delete is a soft delete: the row stays
    // visible (struck through, restorable), so the tab is not "empty" and its
    // Confirm button stays shown — the user confirms the deletion, which is
    // what reconciles the removal into the summary. The only auto-confirmed
    // case is a tab with no rows at all (nothing extracted/added), handled on
    // extract (runExtraction) and on load — a mutation never runs against that
    // state, so it doesn't need handling here.
    function _markEventsEdited() {
        eventsConfirmed.value = false;
    }
    function _markEntitiesEdited() {
        entitiesConfirmed.value = false;
    }

    function acceptEvent(id: string) {
        const row = events.value.find((e) => e.id === id);
        if (row) row.suggestionStatus = 'accepted';
    }

    // Soft delete — see deleteEntity. Rejecting a suggestion or deleting an
    // accepted event marks it; it stays visible and can be restored.
    function rejectEvent(id: string) {
        const row = events.value.find((e) => e.id === id);
        if (row) {
            row.deleted = true;
            _markEventsEdited();
        }
    }

    function confirmEvents() {
        if (!canConfirmEvents.value) return;
        // Bulk-accept any remaining pending suggestions, then lock the tab.
        for (const row of events.value) {
            if (row.suggestionStatus === 'pending') row.suggestionStatus = 'accepted';
        }
        eventsConfirmed.value = true;
    }

    function updateEvent(id: string, patch: Partial<Pick<EventRow, 'date' | 'context'>>) {
        const row = events.value.find((e) => e.id === id);
        if (!row) return;
        if (patch.date !== undefined) row.date = patch.date;
        if (patch.context !== undefined) row.context = patch.context;
        _markEventsEdited();
    }

    function addEvent(date: string, context: string) {
        const trimmed = date.trim();
        const ctx = context.trim();
        if (!trimmed && !ctx) return;
        const id = nextId();
        const base = { date: trimmed, context: ctx };
        events.value.push({
            id,
            slug: `user-${id}`,
            ...base,
            original: snapshotEvent(base),
        });
        _markEventsEdited();
    }

    // Persist a single event to the backend via POST /MatterContext/Event.
    // Stages a draft — copy-on-write for an established item, in-place update
    // for an existing draft — that the publish promotes. Called only from
    // publish()'s queued-change flush (there is no per-item Save in the UI).
    // On success the row's backend ids + slug are refreshed from the response
    // and its baseline is re-snapshotted so the field-level "modified"
    // highlight clears. Returns true on success; on failure errorMessage is
    // set and the row is left untouched (still queued).
    async function persistEventDraft(id: string): Promise<boolean> {
        const row = events.value.find((e) => e.id === id);
        if (!row) return false;
        const matterId = targetMatterId.value;
        if (!matterId) {
            errorMessage.value = 'No matter selected';
            return false;
        }
        try {
            const response = await fetch(`/api/Matter/${matterId}/MatterContext/Event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: row.backendId ?? null,
                    slug: row.slug,
                    date: row.date,
                    context: row.context,
                }),
            });
            if (!response.ok) {
                throw new Error(await _extractErrorMessage(response, 'Save failed'));
            }
            const saved = (await response.json()) as {
                id: string;
                draft_of_id?: string | null;
                slug: string;
                date: string;
                context: string;
            };
            row.backendId = saved.id;
            row.backendDraftOfId = saved.draft_of_id ?? null;
            row.slug = saved.slug;
            row.date = saved.date;
            row.context = saved.context;
            // Re-baseline so the just-saved values no longer read as modified.
            row.original = snapshotEvent(row);
            return true;
        } catch (err) {
            errorMessage.value = err instanceof Error ? err.message : String(err);
            return false;
        }
    }

    function deleteEvent(id: string) {
        const row = events.value.find((e) => e.id === id);
        if (row) {
            row.deleted = true;
            _markEventsEdited();
        }
    }

    function restoreEvent(id: string) {
        const row = events.value.find((e) => e.id === id);
        if (row) {
            row.deleted = false;
            _markEventsEdited();
        }
    }

    // Revert every tracked field on an event to its extract baseline.
    function resetEvent(id: string) {
        const row = events.value.find((e) => e.id === id);
        if (!row) return;
        row.date = row.original.date;
        row.context = row.original.context;
        _markEventsEdited();
    }

    function acceptEntity(id: string) {
        const row = entities.value.find((e) => e.id === id);
        if (row) row.suggestionStatus = 'accepted';
    }

    function rejectEntity(id: string) {
        const row = entities.value.find((e) => e.id === id);
        if (row) {
            row.deleted = true;
            _markEntitiesEdited();
        }
    }

    function confirmEntities() {
        if (!canConfirmEntities.value) return;
        // Bulk-accept any remaining pending suggestions, then lock the tab.
        for (const row of entities.value) {
            if (row.suggestionStatus === 'pending') row.suggestionStatus = 'accepted';
        }
        entitiesConfirmed.value = true;
    }

    function updateEntity(
        id: string,
        patch: Partial<
            Pick<EntityRow, 'entityType' | 'name' | 'context' | 'firstName' | 'lastName' | 'email'>
        >,
    ) {
        const row = entities.value.find((e) => e.id === id);
        if (!row) return;
        if (patch.entityType !== undefined) row.entityType = patch.entityType;
        if (patch.name !== undefined) row.name = patch.name;
        if (patch.context !== undefined) row.context = patch.context;
        if (patch.firstName !== undefined) row.firstName = patch.firstName;
        if (patch.lastName !== undefined) row.lastName = patch.lastName;
        if (patch.email !== undefined) row.email = patch.email;
        _markEntitiesEdited();
    }

    function addEntity(entityType: string, name: string, context: string, email = '') {
        const n = name.trim();
        if (!n) return;
        const id = nextId();
        const base = {
            entityType: entityType.trim() || 'Person',
            name: n,
            context: context.trim(),
            firstName: '',
            lastName: '',
            email: email.trim(),
        };
        entities.value.push({
            id,
            slug: `user-${id}`,
            ...base,
            original: snapshotEntity(base),
        });
        _markEntitiesEdited();
    }

    // Persist a single entity to the backend via POST /MatterContext/Entity,
    // mirroring persistEventDraft (called only from publish()'s queued-change
    // flush). Stages a draft — copy-on-write for an established item,
    // in-place update for an existing draft — that the publish promotes. The
    // display entityType is mapped back to the backend's snake_case value;
    // given_name/family_name are not sent (the backend persists them null for
    // now), so the frontend-only firstName/lastName fields are left as-is.
    // On success the row's backend ids + slug are refreshed from the response
    // and its baseline is re-snapshotted so the field-level "modified"
    // highlight clears. Returns true on success; on failure errorMessage is
    // set and the row is left untouched (still queued).
    async function persistEntityDraft(id: string): Promise<boolean> {
        const row = entities.value.find((e) => e.id === id);
        if (!row) return false;
        const matterId = targetMatterId.value;
        if (!matterId) {
            errorMessage.value = 'No matter selected';
            return false;
        }
        try {
            const response = await fetch(`/api/Matter/${matterId}/MatterContext/Entity`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: row.backendId ?? null,
                    slug: row.slug,
                    entity_type: backendEntityType(row.entityType),
                    name: row.name,
                    email_address: row.email || null,
                    context: row.context,
                }),
            });
            if (!response.ok) {
                throw new Error(await _extractErrorMessage(response, 'Save failed'));
            }
            const saved = (await response.json()) as {
                id: string;
                draft_of_id?: string | null;
                slug: string;
                entity_type: string;
                name: string;
                email_address?: string | null;
                context: string;
            };
            row.backendId = saved.id;
            row.backendDraftOfId = saved.draft_of_id ?? null;
            row.slug = saved.slug;
            row.entityType = displayEntityType(saved.entity_type);
            row.name = saved.name;
            row.email = saved.email_address ?? '';
            row.context = saved.context;
            // Re-baseline so the just-saved values no longer read as modified.
            row.original = snapshotEntity(row);
            return true;
        } catch (err) {
            errorMessage.value = err instanceof Error ? err.message : String(err);
            return false;
        }
    }

    // Soft delete — keep the row so it can be restored; publish skips it.
    function deleteEntity(id: string) {
        const row = entities.value.find((e) => e.id === id);
        if (row) {
            row.deleted = true;
            _markEntitiesEdited();
        }
    }

    function restoreEntity(id: string) {
        const row = entities.value.find((e) => e.id === id);
        if (row) {
            row.deleted = false;
            _markEntitiesEdited();
        }
    }

    // Revert every tracked field on an entity to its extract baseline.
    function resetEntity(id: string) {
        const row = entities.value.find((e) => e.id === id);
        if (!row) return;
        row.name = row.original.name;
        row.context = row.original.context;
        row.entityType = row.original.entityType;
        row.firstName = row.original.firstName;
        row.lastName = row.original.lastName;
        row.email = row.original.email;
        _markEntitiesEdited();
    }

    // Build the JSON publish payload from the current (non-deleted) review
    // state. Shared by publish() and the loaded-context dirty baseline so the
    // two never drift. Relationships whose endpoints were rejected/deleted are
    // pruned (the backend applies the same guard; pruning here keeps the wire
    // payload honest about what's actually published). Deleted rows that map
    // to a persisted backend item are listed in deleted_item_ids so the
    // publish retires them server-side (discarding deleted drafts,
    // superseding deleted published items); deleted rows with no backendId
    // only ever existed locally and need no server-side cleanup.
    function _buildPublishPayload() {
        const publishedSlugs = new Set<string>();
        const deletedItemIds = [...events.value, ...entities.value]
            .filter((r) => r.deleted && r.backendId)
            .map((r) => r.backendId as string);
        const entityPayload = entities.value
            .filter((e) => !e.deleted)
            .map((e) => {
                publishedSlugs.add(e.slug);
                return {
                    slug: e.slug,
                    entity_type: backendEntityType(e.entityType),
                    name: e.name,
                    context: e.context,
                };
            });
        const eventPayload = events.value
            .filter((e) => !e.deleted)
            .map((e) => {
                publishedSlugs.add(e.slug);
                return {
                    slug: e.slug,
                    date: e.date,
                    context: e.context,
                };
            });
        const relationshipPayload = relationships.value
            .filter(
                (r) =>
                    publishedSlugs.has(r.sourceEntitySlug) &&
                    publishedSlugs.has(r.targetEntitySlug),
            )
            .map((r) => ({
                source_entity_slug: r.sourceEntitySlug,
                relation_verb: r.relationVerb,
                target_entity_slug: r.targetEntitySlug,
                detail: r.detail,
            }));

        return {
            summary: summary.value,
            entities: entityPayload,
            events: eventPayload,
            relationships: relationshipPayload,
            deleted_item_ids: deletedItemIds,
        };
    }

    // Stage every queued row change as a backend draft, in row order. Edits
    // and additions are batched locally (no per-item Save in the UI) and only
    // reach the backend here, right before Publish promotes the drafts — the
    // Publish endpoint ignores the payload's entities/events arrays, so the
    // per-item endpoints are the only way queued changes can land. Sequential
    // on purpose: the first upsert finds-or-creates the matter's draft run,
    // and parallel posts could race that. Returns false on the first failure
    // (errorMessage is already set by the persist call); already-flushed rows
    // are harmless — re-publishing just re-stages the remainder.
    async function _flushQueuedRows(): Promise<boolean> {
        for (const row of events.value) {
            if (!isEventQueuedForPublish(row)) continue;
            if (!(await persistEventDraft(row.id))) return false;
        }
        for (const row of entities.value) {
            if (!isEntityQueuedForPublish(row)) continue;
            if (!(await persistEntityDraft(row.id))) return false;
        }
        return true;
    }

    async function publish() {
        if (!canPublish.value || publishing.value) return;
        const matterId = targetMatterId.value;
        if (!matterId) {
            errorMessage.value = 'No matter selected';
            return;
        }

        publishing.value = true;
        errorMessage.value = null;
        try {
            // Fold any inline entity/event edits into the summary text before
            // publishing. This previously fired when both review tabs were
            // confirmed; the Confirm step is gone (the buttons are commented
            // out), so reconciliation now runs once here. No-op (no network
            // call) when nothing changed since the last capture; non-blocking
            // on failure (keeps the existing summary, surfaces a toast).
            await updateSummaryFromChanges();

            // Batched row changes first — Publish only promotes drafts that
            // already exist server-side.
            if (!(await _flushQueuedRows())) return;

            const response = await fetch(`/api/Matter/${matterId}/MatterContext/Publish`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(_buildPublishPayload()),
            });

            if (!response.ok) {
                throw new Error(await _extractErrorMessage(response, 'Publish failed'));
            }

            // Onboarding-funnel completion step. Counts only — no event/entity
            // content (see analytics PII policy). Count included (non-deleted)
            // rows, matching what actually gets published.
            captureEvent('matter_context_published', {
                matter_id: matterId,
                event_count: events.value.filter((e) => !e.deleted).length,
                entity_count: entities.value.filter((e) => !e.deleted).length,
            });

            // Refresh the chat banner state so it reflects the new
            // published snapshot. Best-effort — failure shouldn't
            // block the publish toast.
            try {
                await useChatStore().fetchMatterContextStatus(matterId);
            } catch {
                /* swallow — banner will sync on next session load */
            }

            const t = 'Matter context published';
            toast.value = t;
            close();
            setTimeout(() => {
                if (toast.value === t) toast.value = null;
            }, 3000);
        } catch (err) {
            errorMessage.value = err instanceof Error ? err.message : String(err);
        } finally {
            publishing.value = false;
        }
    }

    function dismissToast() {
        toast.value = null;
    }

    function dismissProgress() {
        if (extractionState.value !== 'complete') return;
        progressDismissed.value = true;
    }

    // Mark a review tab as visited (the user clicked onto it), clearing its
    // unvisited dot. Visiting is no longer a Publish gate — purely visual.
    function markTabVisited(tab: 'events' | 'entities') {
        if (tab === 'events') eventsVisited.value = true;
        else entitiesVisited.value = true;
    }

    // Returns whether the Summary reveal animation should play, clearing the
    // one-shot flag so it doesn't replay on later tab switches.
    function consumeSummaryReveal(): boolean {
        if (!summaryRevealPending.value) return false;
        summaryRevealPending.value = false;
        return true;
    }

    // Show a transient toast that auto-dismisses after `durationMs`. The
    // identity check on dismiss preserves any newer toast that landed in
    // the meantime (mirrors the inline pattern already used by publish()).
    function showToast(message: string, durationMs = 3000) {
        toast.value = message;
        setTimeout(() => {
            if (toast.value === message) toast.value = null;
        }, durationMs);
    }

    // Summary reconciliation (folding inline entity/event edits into the
    // summary text) now runs once inside publish() — see the call there. The
    // previous both-tabs-confirmed watcher was removed along with the per-tab
    // Confirm buttons.

    return {
        visible,
        targetMatterId,
        openedAutomatically,
        matterKnowledge,
        stagedFiles,
        existingDocuments,
        knowledgeEditorOpen,
        knowledgeEditorDraft,
        knowledgeEditorDirty,
        hasUnsavedWork,
        inputsEditable,
        hasUnpublishedReviewEdits,
        extractionState,
        progressPhase,
        etaSecondsRemaining,
        progressDismissed,
        loadedExisting,
        loadingExisting,
        phaseLabel,
        summary,
        summaryRunId,
        summaryUpdating,
        canSubmitSummaryFeedback,
        summaryFeedback,
        summaryFeedbackSubmitting,
        submitSummaryFeedback,
        updateSummaryFromChanges,
        events,
        entities,
        relationships,
        eventsConfirmed,
        entitiesConfirmed,
        eventsVisited,
        entitiesVisited,
        markTabVisited,
        canConfirmEvents,
        canConfirmEntities,
        errorMessage,
        publishing,
        toast,
        eventsHasPending,
        entitiesHasPending,
        hasAnyPending,
        hasInvalidFiles,
        canRunExtraction,
        canPublish,
        open,
        close,
        resetInPlace,
        setMatterKnowledge,
        openKnowledgeEditor,
        saveKnowledgeEditor,
        discardKnowledgeEditor,
        stageFiles,
        removeStagedFile,
        runExtraction,
        acceptEvent,
        rejectEvent,
        confirmEvents,
        updateEvent,
        addEvent,
        deleteEvent,
        restoreEvent,
        resetEvent,
        acceptEntity,
        rejectEntity,
        confirmEntities,
        updateEntity,
        addEntity,
        deleteEntity,
        restoreEntity,
        resetEntity,
        publish,
        dismissToast,
        showToast,
        dismissProgress,
        consumeSummaryReveal,
    };
});
